import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Overlay, vars } from '@trucktrax/trucktrax-common';
import { detect } from 'detect-browser';
import React, { Component } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Redirect, Switch } from 'react-router-dom';
import { applyMiddleware, compose, createStore } from 'redux';
import thunk from 'redux-thunk';

import bgOverlayJpg from './assets/img/bgOverlay.jpg';
import browserSizeSvg from './assets/img/browserSize.svg';
import browserIconsPng from './assets/img/chrome-edge-browser-icons.png';
import PrivateRoute from './components/shared/routing/PrivateRoute';
import DataTableModuleBoundaryReset from './components/shared/DataTableModuleBoundaryReset';
import RouteWithSubRoutes from './components/shared/routing/RouteWithSubRoutes';
import { MESSAGE_WINDOW, MOBILE_LANDING_PATH } from './constants/apiConstants';
import {
  BROWSER_TYPE_NOTIFICATION,
  BROWSER_TYPE_NOTIFICATIONNOLINK,
  BROWSER_WIDTH_ALT,
  BROWSER_WIDTH_TEXT,
} from './constants/appConstants';
import styles from './Main.module.css';
import { privateRoutes, publicRoutes } from './routes';
import { rootReducer } from './store';
import muiStyleOverrides from './styles/MuiStyleOverrides';
import { ConnectedFlagsProvider } from './util/flagUtil';

const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
export const store = createStore(rootReducer, composeEnhancers(applyMiddleware(thunk)));

// MUI v5: construct theme directly (adaptV4Theme is deprecated)
const baseTheme = createTheme({
  typography: {
    body1: {
      fontFamily: vars.fontFamilyRegular,
    },
    htmlFontSize: 10,
  },
  palette: {
    primary: {
      main: '#008EF3',
    },
  },
});

const appTheme = createTheme(baseTheme, muiStyleOverrides);

class Main extends Component {
  // just so we can change the value easily in tests
  static getWindowUrl = () => window.location.href;

  // Resolves the current path from the (possibly fully-qualified) window URL.
  static getWindowPath = () =>
    Main.getWindowUrl()
      .replace(/^https?:\/\/[^/]+/, '')
      .split(/[?#]/)[0];

  // Matches the non-exact `/mobile` route — `/mobile`, `/mobile/`, `/mobile/...` — without
  // catching unrelated paths that merely end in "/mobile" (e.g. /geotrax/docs/guide/mobile).
  static isMobileLandingPath = () => {
    const path = Main.getWindowPath();
    return path === MOBILE_LANDING_PATH || path.startsWith(`${MOBILE_LANDING_PATH}/`);
  };

  static browserOverlay = () => {
    // Overlay exemptions are two intentionally-distinct checks: MESSAGE_WINDOW (substring match on
    // the full URL, below) and /mobile (parsed-path prefix, here). They are NOT unified because the
    // semantics differ. If a third exemption appears, extract a single parsed-path
    // isOverlayExemptPath(path) predicate and migrate the messages check onto it.
    //
    // The public /mobile landing page is built for phones (DEV-3191) — never gate it behind the
    // desktop browser-type or size overlays, which would block iOS Safari and small viewports.
    if (Main.isMobileLandingPath()) {
      return null;
    }

    let browserOverlay = null;
    const browser = detect();
    const chromeLink = (
      <a style={{ color: '#17A9FF' }} href="https://www.google.com/chrome/">
        {' '}
        Google Chrome{' '}
      </a>
    );
    const edgeLink = (
      <a style={{ color: '#17A9FF' }} href="https://www.microsoft.com/en-us/edge">
        Microsoft Edge.
      </a>
    );

    if (browser && (browser.name === 'chrome' || browser.name === 'edge-chromium')) {
      // don't use the size warning overlay for the messages window
      if (!Main.getWindowUrl().includes(MESSAGE_WINDOW)) {
        browserOverlay = (
          <Overlay
            bgImageSrc={bgOverlayJpg}
            imageSrc={browserSizeSvg}
            text={BROWSER_WIDTH_TEXT}
            isSizeOverlay
            altText={BROWSER_WIDTH_ALT}
          />
        );
      }
    } else {
      browserOverlay = (
        <Overlay
          bgImageSrc={bgOverlayJpg}
          imageSrc={browserIconsPng}
          text={
            <div>
              {BROWSER_TYPE_NOTIFICATION} {chromeLink} or {edgeLink}
            </div>
          }
          altText={BROWSER_TYPE_NOTIFICATIONNOLINK}
        />
      );
    }
    return browserOverlay;
  };

  render() {
    return (
      <ThemeProvider theme={appTheme}>
        {/* @ts-ignore */}
        <Provider store={store}>
          {/* @ts-ignore */}
          <ConnectedFlagsProvider>
            <div className={styles.overlayContainer}>
              {Main.browserOverlay()}
              <Router>
                <DataTableModuleBoundaryReset />
                <Switch>
                  {publicRoutes.map(publicRoute => {
                    const { key: _key, ...routeProps } = publicRoute as any;
                    const routeKey = _key ?? publicRoute.path ?? JSON.stringify(publicRoute);
                    return <RouteWithSubRoutes key={routeKey} {...routeProps} />;
                  })}
                  {privateRoutes.map(privateRoute => {
                    const { key: _key, ...routeProps } = privateRoute as any;
                    const routeKey =
                      _key ?? (privateRoute as any).path ?? JSON.stringify(privateRoute);
                    return <PrivateRoute key={routeKey} {...(routeProps as any)} />;
                  })}
                  <Redirect key="redirect-route" to="/404" />
                </Switch>
              </Router>
            </div>
          </ConnectedFlagsProvider>
        </Provider>
      </ThemeProvider>
    );
  }
}

export default Main;
